import { useCallback, useEffect, useState } from "react";
import { coachApi } from "@/api";
import type { DashboardData } from "@/lib/types";
import Dashboard from "./Dashboard";
import CoachCalendar from "./CoachCalendar";
import CoachNotes from "./CoachNotes";
import CoachPlanChat from "./CoachPlanChat";
import DirectChat from "./DirectChat";
import { useUI } from "@/lib/theme";

type Section = "fitness" | "salud" | "plan" | "notas" | "ia" | "msg";

export default function CoachAthleteView({
  athleteId,
  onBack,
  initialSection = "fitness",
}: {
  athleteId: string;
  onBack: () => void;
  initialSection?: Section;
}) {
  const { t } = useUI();
  const [section, setSection] = useState<Section>(initialSection);
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setError(false);
    coachApi
      .athleteDashboard(athleteId)
      .then(setData)
      .catch(() => setError(true));
  }, [athleteId]);

  useEffect(() => { load(); }, [load]);

  const athleteName = data
    ? [data.athlete.first_name, data.athlete.last_name].filter(Boolean).join(" ") || "Atleta"
    : "Atleta";

  if (error || (!data && (section === "fitness" || section === "salud"))) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-6 text-center text-sm text-muted">
        {error ? "Error al cargar el perfil del atleta." : t("admin_loading")}
      </div>
    );
  }

  const SECTIONS: { value: Section; label: string }[] = [
    { value: "fitness", label: t("seg_fitness") },
    { value: "salud",   label: t("seg_salud") },
    { value: "plan",    label: t("tab_calendario") },
    { value: "notas",   label: t("tab_notes") },
    { value: "msg",     label: t("msg_tab") },
    { value: "ia",      label: t("coach_title") },
  ];

  return (
    <div>
      {/* Cabecera de la vista del atleta: back + avatar + nombre + nav de secciones */}
      <div className="sticky top-0 z-30 border-b border-line bg-panel/90 backdrop-blur">
        <div className="px-4 pt-3">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="flex shrink-0 items-center gap-1 rounded-lg border border-line px-2.5 py-1 text-xs font-medium text-muted transition hover:text-fg"
            >
              ← {t("coach_subtab_athletes")}
            </button>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-bold text-white">
              {athleteName.slice(0, 1).toUpperCase()}
            </span>
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-lg font-extrabold tracking-tight text-fg">{athleteName}</span>
              <span className="text-[11px] text-muted">{t("profile_role_athlete")}</span>
            </div>
          </div>
        </div>
        <nav className="flex gap-0.5 overflow-x-auto px-4 py-2">
          {SECTIONS.map((s) => (
            <button
              key={s.value}
              onClick={() => setSection(s.value)}
              className={`whitespace-nowrap rounded-lg px-3 py-1 text-sm font-semibold transition ${section === s.value ? "bg-accent/15 text-accent" : "text-muted hover:text-fg"}`}
            >
              {s.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Contenido */}
      {(section === "fitness" || section === "salud") && data && (
        <Dashboard
          key={section}
          data={data}
          readOnly
          viewerRole="coach"
          onLogout={() => {}}
          onReload={load}
          defaultSection={section}
        />
      )}
      {section === "plan" && (
        <div className="mx-auto max-w-6xl px-4 py-6">
          <CoachCalendar athleteId={athleteId} />
        </div>
      )}
      {section === "notas" && (
        <div className="mx-auto max-w-6xl px-4 py-6">
          <CoachNotes athleteId={athleteId} />
        </div>
      )}
      {section === "ia" && (
        <div className="mx-auto max-w-6xl px-4 py-6">
          <CoachPlanChat athleteId={athleteId} />
        </div>
      )}
      {section === "msg" && (
        <div className="mx-auto max-w-6xl px-4 py-6">
          <DirectChat
            viewerRole="coach"
            fetchMessages={() => coachApi.messages(athleteId)}
            sendMessage={(body) => coachApi.sendMessage(athleteId, body)}
            peer={{ name: athleteName }}
          />
        </div>
      )}
    </div>
  );
}
